import { useCallback, useEffect, useState } from "react";
import { Eye, CheckCircle2, XCircle, Plane } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt, daysBetween, hoursOf } from "@/lib/dates";
import { HOLIDAY } from "@/lib/colors";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { RosterReviewDialog } from "@/components/roster/RosterReviewDialog";
import { ClashWarning } from "@/components/holidays/ClashWarning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const RosterItem = ({ r, onReview, onApprove }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3 fade-up" data-testid={`approval-roster-${r.id}`}>
    <div className="flex-1">
      <div className="font-bold text-slate-800">{r.manager?.name} <span className="text-slate-400 font-normal">· {r.manager?.department}</span></div>
      <div className="text-sm text-slate-500">Week of {fmt(r.week_start, "d MMM")} · {r.shifts.length} shifts · {r.shifts.reduce((n, s) => n + hoursOf(s), 0).toFixed(1)} h · {r.shifts.filter((s) => s.location === "remote").length} remote</div>
    </div>
    <div className="flex gap-2">
      <Button variant="outline" className="rounded-full" onClick={() => onReview(r)} data-testid={`approval-roster-review-${r.id}`}><Eye className="w-4 h-4 mr-1.5" /> Review & edit</Button>
      <Button className="rounded-full" onClick={() => onApprove(r)} data-testid={`approval-roster-approve-${r.id}`}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve</Button>
    </div>
  </div>
);

const HolidayItem = ({ h, onDecide }) => {
  const [comment, setComment] = useState("");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 fade-up" data-testid={`approval-holiday-${h.id}`}>
      <div className="flex items-start gap-3">
        <div className={`holiday-stripes w-10 h-10 rounded-xl border grid place-items-center shrink-0 ${HOLIDAY[h.type].cls}`}><Plane className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="font-bold text-slate-800">{h.user_name} <span className="text-slate-400 font-normal">· {h.department}</span></div>
          <div className="text-sm text-slate-600">{HOLIDAY[h.type].label} · {fmt(h.start_date, "EEE d MMM")} – {fmt(h.end_date, "EEE d MMM")} ({daysBetween(h.start_date, h.end_date)} day(s))</div>
          {h.note && <div className="text-sm text-slate-500 mt-0.5">“{h.note}”</div>}
        </div>
      </div>
      <ClashWarning clashes={h.clashes} testid={`approval-holiday-clash-${h.id}`} />
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`approval-holiday-comment-${h.id}`} />
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => onDecide(h, "declined", comment)} data-testid={`approval-holiday-decline-${h.id}`}><XCircle className="w-4 h-4 mr-1.5" /> Decline</Button>
          <Button className="rounded-full" onClick={() => onDecide(h, "approved", comment)} data-testid={`approval-holiday-approve-${h.id}`}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve</Button>
        </div>
      </div>
    </div>
  );
};

export default function Approvals() {
  const { user } = useAuth();
  const [data, setData] = useState({ rosters: [], holidays: [] });
  const [mineOnly, setMineOnly] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/approvals");
    setData(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rosters = data.rosters.filter((r) => !mineOnly || r.manager?.supervisor_id === user.id);
  const holidays = data.holidays.filter((h) => !mineOnly || h.supervisor_id === user.id);

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Schedules for this week and next, plus pending leave requests.">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="approvals-my-team-toggle" /> My team only
        </label>
      </PageHeader>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section>
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">Submitted schedules <span className="text-slate-400">({rosters.length})</span></h2>
          <div className="space-y-3" data-testid="approvals-rosters-list">
            {rosters.map((r) => <RosterItem key={r.id} r={r} onReview={setReviewing} onApprove={(x) => call(() => api.post(`/rosters/${x.id}/approve`), "Schedule approved")} />)}
            {rosters.length === 0 && <Empty testid="approvals-rosters-empty">No schedules waiting for review.</Empty>}
          </div>
        </section>
        <section>
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">Holiday requests <span className="text-slate-400">({holidays.length})</span></h2>
          <div className="space-y-3" data-testid="approvals-holidays-list">
            {holidays.map((h) => (
              <HolidayItem key={h.id} h={h} onDecide={(x, status, comment) => call(() => api.post(`/holidays/${x.id}/decide`, { status, comment }), status === "approved" ? "Leave approved" : "Leave declined")} />
            ))}
            {holidays.length === 0 && <Empty testid="approvals-holidays-empty">No pending leave requests.</Empty>}
          </div>
        </section>
      </div>
      <RosterReviewDialog roster={reviewing} manager={reviewing?.manager} onClose={() => setReviewing(null)} onDone={load} />
    </div>
  );
}
